import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { studentsAPI, placementsAPI, feedbackFormsAPI, feedbackTemplatesAPI } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/dateUtils';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Mail, Phone, Calendar, Hash, GraduationCap, Building2, Briefcase, MessageSquare, Edit2, ClipboardCheck, Star, Eye, Download, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const StudentProfile = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [student, setStudent] = useState(null);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedbackModal, setFeedbackModal] = useState({ open: false, placement: null });
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackForms, setFeedbackForms] = useState([]);
  const [submissions, setSubmissions] = useState([]);

  // Determine back navigation based on current path
  const isFromRecords = location.pathname.includes('/student-records/');
  const backPath = isFromRecords ? '/student-records' : '/students';
  const backLabel = isFromRecords ? 'Back to Student Records' : 'Back to Students';

  useEffect(() => {
    fetchData();
  }, [studentId]);

  const fetchData = async () => {
    try {
      const [studentRes, placementsRes] = await Promise.all([
        studentsAPI.getOne(studentId),
        studentsAPI.getPlacements(studentId),
      ]);
      setStudent(studentRes.data);
      setPlacements(placementsRes.data);

      // Fetch feedback forms and submissions
      try {
        const [formsRes, submissionsRes] = await Promise.all([
          feedbackFormsAPI.getAll({ student_id: studentId }),
          feedbackFormsAPI.getStudentSubmissions(studentId),
        ]);
        setFeedbackForms(formsRes.data || []);
        setSubmissions(submissionsRes.data || []);
      } catch {
        // Feedback data is supplementary
      }
    } catch (error) {
      toast.error('Failed to fetch student data');
      navigate(backPath);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFeedback = async () => {
    try {
      await placementsAPI.update(feedbackModal.placement.id, { feedback: feedbackText });
      toast.success('Feedback saved successfully');
      setFeedbackModal({ open: false, placement: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to save feedback');
    }
  };

  const openFeedbackModal = (placement) => {
    setFeedbackText(placement.feedback || '');
    setFeedbackModal({ open: true, placement });
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      completed: 'bg-blue-100 text-blue-700 border-blue-200',
      cancelled: 'bg-red-100 text-red-700 border-red-200',
      graduated: 'bg-blue-100 text-blue-700 border-blue-200',
      inactive: 'bg-gray-100 text-gray-600 border-gray-200',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${variants[status] || variants.active}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getYearBadge = (year) => {
    const variants = {
      '1st Year': 'bg-blue-50 text-blue-700',
      '2nd Year': 'bg-purple-50 text-purple-700',
      '3rd Year': 'bg-ica-gold/10 text-ica-gold-dark',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded text-sm font-medium ${variants[year] || variants['1st Year']}`}>
        {year}
      </span>
    );
  };

  const currentPlacement = placements.find(p => p.status === 'active' || p.status === 'pending');
  const pastPlacements = placements.filter(p => p.status === 'completed' || p.status === 'cancelled');

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
        </div>
      </Layout>
    );
  }

  if (!student) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Student not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="student-profile-page" className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(backPath)}
            className="text-gray-600 hover:text-ica-navy"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {backLabel}
          </Button>
          <Button
            data-testid="view-performance-report"
            variant="outline"
            onClick={() => navigate(`/students/${studentId}/performance`)}
            className="border-ica-navy text-ica-navy hover:bg-ica-navy/5"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Performance Report
          </Button>
        </div>

        {/* Student Info Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-ica-navy/10 rounded-full flex items-center justify-center text-ica-navy text-3xl font-semibold">
                  {student.name.charAt(0).toUpperCase()}
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl font-bold text-ica-navy">{student.name}</h1>
                  {getStatusBadge(student.status)}
                  {getYearBadge(student.year_of_study || '1st Year')}
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                    {student.course_length || '3 Year'} Course
                  </span>
                  {student.course_type && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-ica-gold/10 text-ica-gold-dark">
                      {student.course_type}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Hash className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">Student #:</span>
                    <span>{student.student_number || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{student.email}</span>
                  </div>
                  {student.phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span>{student.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">Enrolled:</span>
                    <span>{formatDate(student.enrollment_date)}</span>
                  </div>
                </div>

                {/* Preferences */}
                {student.preferences && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">Preferences</p>
                    <p className="text-gray-600">{student.preferences}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Current Placement */}
        {currentPlacement && (
          <Card className="border-l-4 border-l-ica-gold">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-ica-navy">
                <Briefcase className="w-5 h-5" />
                Current Placement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-ica-gold" />
                    <span className="font-semibold text-lg">{currentPlacement.restaurant_name}</span>
                    {getStatusBadge(currentPlacement.status)}
                  </div>
                  <p className="text-gray-600">
                    <span className="font-medium">Position:</span> {currentPlacement.position}
                  </p>
                  <p className="text-gray-500 text-sm">
                    {formatDate(currentPlacement.start_date)} - {formatDate(currentPlacement.end_date)}
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => openFeedbackModal(currentPlacement)}
                  className="border-ica-navy text-ica-navy"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  {currentPlacement.feedback ? 'View/Edit Feedback' : 'Add Feedback'}
                </Button>
              </div>
              {currentPlacement.feedback && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-500 mb-1">Feedback</p>
                  <p className="text-gray-700">{currentPlacement.feedback}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Placement History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-ica-navy">
              <GraduationCap className="w-5 h-5" />
              Placement History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pastPlacements.length === 0 && !currentPlacement ? (
              <p className="text-gray-500 text-center py-8">No placement history yet.</p>
            ) : (
              <div className="space-y-4">
                {pastPlacements.map((placement) => (
                  <div
                    key={placement.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-ica-gold" />
                          <span className="font-semibold">{placement.restaurant_name}</span>
                          {getStatusBadge(placement.status)}
                        </div>
                        <p className="text-gray-600">
                          <span className="font-medium">Position:</span> {placement.position}
                        </p>
                        <p className="text-gray-500 text-sm">
                          {formatDate(placement.start_date)} - {formatDate(placement.end_date)}
                          <span className="ml-2 text-ica-gold">
                            ({placement.start_date?.split('-')[0] || ''})
                          </span>
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openFeedbackModal(placement)}
                        className="text-gray-600 hover:text-ica-navy"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        {placement.feedback ? 'Edit Feedback' : 'Add Feedback'}
                      </Button>
                    </div>
                    {placement.feedback && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm font-medium text-gray-500 mb-1">Partner Feedback</p>
                        <p className="text-gray-700 text-sm">{placement.feedback}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Digital Feedback Submissions */}
        {submissions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-ica-navy">
                <ClipboardCheck className="w-5 h-5" />
                Chef Assessment Reports
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {submissions.map((sub) => {
                  const form = feedbackForms.find(f => f.id === sub.form_id);
                  const ratingKeys = Object.keys(sub.responses || {}).filter(k => !k.endsWith('_comment'));
                  const ratingValues = ratingKeys
                    .map(k => sub.responses[k])
                    .filter(v => typeof v === 'number');
                  const avgRating = ratingValues.length > 0
                    ? (ratingValues.reduce((a, b) => a + b, 0) / ratingValues.length).toFixed(1)
                    : null;

                  return (
                    <div key={sub.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-800">
                              Assessment #{form?.assessment_number || '?'}
                            </span>
                            {form && (
                              <span className="text-xs text-gray-500">
                                ({form.template_name})
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span>Chef: {sub.chef_name}</span>
                            {form && <span>Restaurant: {form.restaurant_name}</span>}
                            <span>
                              {sub.submitted_at
                                ? formatDate(sub.submitted_at)
                                : ''}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {avgRating && (
                            <div className="flex items-center gap-1 px-3 py-1 bg-ica-gold/10 rounded-full">
                              <Star className="w-3.5 h-3.5 text-ica-gold" />
                              <span className="font-semibold text-ica-gold-dark text-sm">{avgRating}/10</span>
                            </div>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/feedback-view/${sub.form_id}`)}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`download-pdf-${sub.form_id}`}
                            onClick={() => navigate(`/feedback-view/${sub.form_id}?download=pdf`)}
                          >
                            <Download className="w-3.5 h-3.5 mr-1" />
                            PDF
                          </Button>
                        </div>
                      </div>
                      {sub.strengths && (
                        <div className="mt-3 p-3 bg-emerald-50 rounded text-sm">
                          <span className="font-medium text-emerald-700">Strengths: </span>
                          <span className="text-emerald-800">{sub.strengths}</span>
                        </div>
                      )}
                      {sub.weaknesses && (
                        <div className="mt-2 p-3 bg-amber-50 rounded text-sm">
                          <span className="font-medium text-amber-700">Areas for improvement: </span>
                          <span className="text-amber-800">{sub.weaknesses}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feedback Modal */}
        <Dialog open={feedbackModal.open} onOpenChange={(open) => setFeedbackModal({ ...feedbackModal, open })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-ica-navy">
                Placement Feedback
              </DialogTitle>
              <DialogDescription className="text-gray-500">
                {feedbackModal.placement && (
                  <>
                    {feedbackModal.placement.restaurant_name} - {feedbackModal.placement.position}
                    <br />
                    <span className="text-sm">
                      {formatDate(feedbackModal.placement?.start_date)} - {formatDate(feedbackModal.placement?.end_date)}
                    </span>
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Partner Feedback
                </label>
                <Textarea
                  data-testid="feedback-text"
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Enter feedback from the placement partner about the student's performance..."
                  rows={5}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFeedbackModal({ open: false, placement: null })}>
                Cancel
              </Button>
              <Button
                data-testid="save-feedback-btn"
                onClick={handleSaveFeedback}
                
              >
                Save Feedback
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default StudentProfile;
